import React from 'react';
import {
  X,
  Sparkles,
  ShoppingBag,
  HeartPulse,
  Headphones,
  CheckCircle2,
  ArrowRight,
  Zap,
  ShieldCheck,
  Layers
} from 'lucide-react';
import { INDUSTRY_BLUEPRINTS, IndustryFlowBlueprint } from '../flowTemplates';

interface IndustryBlueprintsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectBlueprint: (blueprint: IndustryFlowBlueprint) => void;
}

export const IndustryBlueprintsModal: React.FC<IndustryBlueprintsModalProps> = ({
  isOpen,
  onClose,
  onSelectBlueprint,
}) => {
  if (!isOpen) return null;

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'ShoppingBag':
        return <ShoppingBag className="text-emerald-400" size={22} />;
      case 'HeartPulse':
        return <HeartPulse className="text-cyan-400" size={22} />;
      case 'Headphones':
        return <Headphones className="text-purple-400" size={22} />;
      default:
        return <Sparkles className="text-cyan-400" size={22} />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-4xl bg-gradient-to-b from-slate-900 via-slate-900/95 to-slate-950 border border-cyan-500/30 rounded-3xl shadow-[0_0_60px_rgba(6,182,212,0.25)] flex flex-col overflow-hidden max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between shrink-0 bg-slate-900/60">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-cyan-500/15 border border-cyan-400/40 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.3)]">
              <Layers size={22} />
            </div>
            <div>
              <h2 className="text-lg font-black text-white flex items-center gap-2">
                Industry-Grade Flow Blueprints
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 uppercase">
                  1-Click Auto-Star Topology
                </span>
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Select a production-tested AI agent architecture pre-wired to Luna Core Hub with zero configuration needed.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Blueprints Grid */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-5 overflow-y-auto">
          {INDUSTRY_BLUEPRINTS.map((bp) => (
            <div
              key={bp.id}
              className="flex flex-col justify-between p-5 rounded-2xl bg-slate-900/80 border border-slate-700/60 hover:border-cyan-400/80 transition-all hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(6,182,212,0.2)] group relative overflow-hidden"
            >
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2.5 rounded-xl bg-slate-950/80 border border-slate-700 group-hover:border-cyan-500/40 transition-colors">
                    {getIcon(bp.iconName)}
                  </div>
                  <span className={`text-[9px] font-black uppercase font-mono px-2 py-0.5 rounded-md border ${bp.badgeColor}`}>
                    {bp.badge}
                  </span>
                </div>

                <h3 className="text-sm font-black text-white group-hover:text-cyan-300 transition-colors mb-2">
                  {bp.title}
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed mb-4">
                  {bp.description}
                </p>

                <div className="p-2.5 rounded-xl bg-slate-950/60 border border-white/5 mb-4">
                  <span className="text-[10px] font-semibold text-cyan-300 block mb-1">
                    ✨ Architecture Highlights:
                  </span>
                  <span className="text-[11px] text-slate-300 leading-snug block">
                    {bp.highlight}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                    {bp.nodes.length} Nodes
                  </span>
                  <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                    {bp.edges.length} Star Edges
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => onSelectBlueprint(bp)}
                className="w-full py-2.5 px-4 rounded-xl font-bold text-xs bg-gradient-to-r from-cyan-600 via-teal-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white shadow-lg flex items-center justify-center gap-2 group-hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all cursor-pointer"
              >
                <span>Instantiate Blueprint</span>
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400 bg-slate-900/60 shrink-0">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={14} className="text-emerald-400" />
            <span>Strict Zero-Regression Guarantee — Instantiating a template safely replaces the canvas graph.</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-xl border border-slate-700 hover:bg-slate-800 text-slate-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
